import { GENERATED_ART } from '@/data/generatedArt'
import { furnishingById, roomTierName, type RoomMount } from '@/data/room'
import { unpackDecor } from '@/data/placement'
import decorBanner from '@/assets/decorations/banner.png'
import decorBooks from '@/assets/decorations/books.png'
import decorChest from '@/assets/decorations/chest.png'
import decorPlant from '@/assets/decorations/plant.png'
import decorStool from '@/assets/decorations/stool.png'

// The Upper Room and its furnishings — drawn, not generated.
//
// Same constraints as KeepArt and ChurchArt, and they are not stylistic: flat
// fills only, no <defs>, no gradients or filters. Two reasons here, and the
// second one is new:
//
//   1. Shared <defs> ids across SVG instances is a classic way to get one
//      instance silently painting another's colours, and this scene renders
//      twice on one screen when you visit a room from your own profile.
//   2. THE POSTCARD — no longer a hard constraint, but read this before
//      assuming it never was. lib/postcard.ts serialises this scene into an
//      <img>, and an SVG loaded that way never fetches external resources, so
//      a room made of <image href> used to export blank; furnishings therefore
//      had to be drawn. The postcard INLINES images as data: URIs now, so a
//      raster furnishing survives the export, and anything that fails to fetch
//      is dropped back to the drawn piece underneath.
//
//      What has NOT changed is that a drawn fallback still has to exist. The
//      rule everywhere here is that generated art layers OVER a drawing rather
//      than instead of it — the churchyard's dev guard caught six plants going
//      in raster-only, and a furnishing with no drawing is an empty anchor the
//      moment a PNG 404s.
//
// The room is a 560x300 interior. Props are drawn around their GROUND POINT
// (0,0 = where they meet their anchor) and translated into place, so a prop
// never needs to know where it stands. Only `wall` pieces straddle the point.

// ── Palette ─────────────────────────────────────────────────────────────────
// Warm clay and lamplight, deliberately unlike the keep's cool stone: these are
// two different places and they should not read as one room recoloured.
const CLAY = '#6f5a44'
const CLAY_DARK = '#54432f'
const PLASTER = '#8a7358'
const FLOOR = '#5a4732'
const FLOOR_DARK = '#463627'
const WOOD = '#6b4f30'
const WOOD_DARK = '#4a3722'
const NIGHT = '#241a3a'
const STAR = '#f4e3a1'
const FLAME = '#ff9f1c'
const FLAME_HOT = '#ffd23f'
const GOLD = '#e8b64c'
const GOLD_DEEP = '#c08a2a'
const CLOTH = '#9c5f4a'
const CLOTH_2 = '#4f6f6a'
const LINEN = '#e4d6b4'
const IRON = '#6a7080'
const BRASS = '#c9a24a'
const GREEN = '#6f8f52'

/** Five rooms, each a real change of silhouette rather than a recolour. */
interface RoomTierStyle {
  wall: string
  wallDark: string
  floor: string
  floorDark: string
  /** Plastered walls instead of bare brick coursing. */
  plastered: boolean
  /** A real arched window rather than a slit. */
  window: boolean
  /** Ceiling beams. */
  beams: boolean
  /** A second window, and a step up into the alcove. */
  upper: boolean
  /** Gilt trim, and the parapet outside. */
  gilt: boolean
}

const TIERS: RoomTierStyle[] = [
  { wall: '#4c3d2c', wallDark: '#3a2f22', floor: '#3f3324', floorDark: '#2f261b',
    plastered: false, window: false, beams: false, upper: false, gilt: false },
  { wall: '#5b4a35', wallDark: '#463829', floor: '#4a3b2a', floorDark: '#382c1f',
    plastered: true, window: false, beams: false, upper: false, gilt: false },
  { wall: '#6a5740', wallDark: '#514231', floor: '#54432f', floorDark: '#3f3223',
    plastered: true, window: true, beams: false, upper: false, gilt: false },
  { wall: '#77624a', wallDark: '#5b4a38', floor: '#5d4a34', floorDark: '#463726',
    plastered: true, window: true, beams: true, upper: true, gilt: false },
  { wall: '#846d52', wallDark: '#65523d', floor: '#65503a', floorDark: '#4c3c2a',
    plastered: true, window: true, beams: true, upper: true, gilt: true },
]

export const roomTierCount = TIERS.length

/**
 * The drawn chamber, with today's painting (roomScenes.ts) laid over it. The
 * drawing is the fallback — a painting that fails to load still leaves a room —
 * and it is all the postcard (`flat`) can serialise.
 */
export function RoomChamber({
  tier = 0,
  flat = false,
  scene,
}: {
  tier?: number
  flat?: boolean
  /** Today's whole-room painting (roomScenes.ts). */
  scene?: string | null
}) {
  const t = Math.min(TIERS.length - 1, Math.max(0, tier))
  const painting = flat ? null : scene ?? null
  return (
    <>
      <DrawnChamber tier={t} />
      {painting && (
        <image
          href={painting}
          x="0"
          y="0"
          width="560"
          height="300"
          preserveAspectRatio="xMidYMid slice"
        />
      )}
    </>
  )
}

/**
 * The room itself. Everything a furnishing can stand on is drawn here.
 *
 * THREE FIXTURES, THREE BANDS OF THE BACK WALL, and they must never overlap:
 * the shelf on the left (110..214), the window right of centre (400..460), the
 * alcove on the far right (470..540). The first version put the window and the
 * alcove on top of each other and drew two arches in the same place; the anchor
 * table in data/room.ts carries the same warning.
 */
// The tier decides the room's shape (plaster, window, beams, gilt) and its
// colours; `pal` is the tier's walls and floor plus the fixture wood.
function DrawnChamber({ tier }: { tier: number }) {
  const s = TIERS[tier]
  const pal = { ...TIERS[tier], wood: WOOD, woodDark: WOOD_DARK }
  return (
    <g>
      {/* Back wall + floor */}
      <rect x="0" y="0" width="560" height="300" fill={pal.wall} />
      {/* The wall darkens toward the ceiling, so a flat fill doesn't read as a
          blank rectangle behind everything. */}
      <rect x="0" y="0" width="560" height="46" fill={pal.wallDark} />
      <rect x="0" y="46" width="560" height="10" fill={pal.wallDark} opacity="0.45" />
      <rect x="0" y="216" width="560" height="84" fill={pal.floor} />
      <rect x="0" y="216" width="560" height="6" fill={pal.floorDark} />

      {/* Brick coursing on the bare chamber; smooth plaster above it. */}
      {!s.plastered && (
        <g stroke={pal.wallDark} strokeWidth="1.4" opacity="0.65">
          {[62, 92, 122, 152, 182].map((y) => (
            <line key={y} x1="0" y1={y} x2="560" y2={y} />
          ))}
          {[0, 1, 2, 3, 4].map((r) =>
            [0, 1, 2, 3, 4, 5, 6, 7].map((c) => (
              <line
                key={`${r}-${c}`}
                x1={c * 70 + (r % 2 ? 35 : 0)}
                y1={62 + r * 30}
                x2={c * 70 + (r % 2 ? 35 : 0)}
                y2={92 + r * 30}
              />
            )),
          )}
        </g>
      )}

      {/* Floorboards, once the floor is boarded rather than beaten earth. */}
      {s.plastered && (
        <g stroke={pal.floorDark} strokeWidth="1.2" opacity="0.5">
          {[236, 256, 278, 296].map((y) => (
            <line key={y} x1="0" y1={y} x2="560" y2={y} />
          ))}
        </g>
      )}

      {/* Ceiling beams */}
      {s.beams && (
        <g>
          <rect x="0" y="0" width="560" height="15" fill={pal.wood} />
          <g fill={pal.woodDark}>
            {[70, 190, 310, 430].map((x) => (
              <rect key={x} x={x} y="0" width="16" height="34" />
            ))}
          </g>
        </g>
      )}

      {/* THE WINDOW — 400..460. A slit in the bare chamber; a real arch from
          the Lit Chamber up. Night sky, because the app is dark and a bright
          noon window would be the brightest thing on the screen by a mile. */}
      {s.window ? (
        <g>
          <path d="M400 150 v-52 a30 30 0 0 1 60 0 v52 z" fill={NIGHT} />
          <path
            d="M400 150 v-52 a30 30 0 0 1 60 0 v52 z"
            fill="none"
            stroke={s.gilt ? GOLD_DEEP : pal.wallDark}
            strokeWidth="5"
          />
          <circle cx="444" cy="110" r="6.5" fill={STAR} opacity="0.85" />
          <circle cx="415" cy="126" r="2" fill={STAR} opacity="0.7" />
          <circle cx="428" cy="96" r="1.6" fill={STAR} opacity="0.55" />
          {s.upper && <rect x="428" y="72" width="4" height="78" fill={pal.wallDark} />}
          {/* The sill the sill-mount anchor stands on. */}
          <rect x="392" y="148" width="76" height="7" rx="2" fill={s.gilt ? GOLD_DEEP : WOOD} />
          <rect x="392" y="155" width="76" height="3" fill={pal.woodDark} />
        </g>
      ) : (
        <g>
          <rect x="420" y="86" width="20" height="58" rx="9" fill={NIGHT} />
          <circle cx="430" cy="104" r="2" fill={STAR} opacity="0.6" />
          <rect x="396" y="146" width="68" height="7" rx="2" fill={pal.woodDark} />
        </g>
      )}

      {/* THE SHELF — 110..214 on the left wall. Two anchors stand on it. */}
      <g>
        <rect x="110" y="126" width="104" height="7" rx="2" fill={pal.wood} />
        <rect x="110" y="133" width="104" height="3" fill={pal.woodDark} />
        <rect x="118" y="136" width="6" height="10" fill={pal.woodDark} />
        <rect x="200" y="136" width="6" height="10" fill={pal.woodDark} />
        {s.gilt && <rect x="110" y="123.5" width="104" height="2.5" fill={GOLD_DEEP} />}
      </g>

      {/* THE ALCOVE — 470..540 on the far right, clear of the window. */}
      <g>
        <path d="M470 248 v-62 a35 35 0 0 1 70 0 v62 z" fill={pal.wallDark} />
        <path d="M474 248 v-60 a31 31 0 0 1 62 0 v60 z" fill="#241d15" opacity="0.7" />
        <path
          d="M470 248 v-62 a35 35 0 0 1 70 0 v62 z"
          fill="none"
          stroke={s.gilt ? GOLD_DEEP : pal.floorDark}
          strokeWidth="4"
        />
        {s.upper && <rect x="464" y="246" width="82" height="8" rx="2" fill={pal.floorDark} />}
      </g>

      {/* THE LOW TABLE, mid-floor. Two anchors stand on its top. */}
      <g>
        <rect x="262" y="212" width="104" height="9" rx="3" fill={pal.wood} />
        <rect x="262" y="221" width="104" height="4" fill={pal.woodDark} />
        <rect x="272" y="225" width="8" height="28" fill={pal.woodDark} />
        <rect x="348" y="225" width="8" height="28" fill={pal.woodDark} />
        {s.gilt && <rect x="262" y="209.5" width="104" height="2.5" fill={GOLD_DEEP} />}
      </g>

      {/* Gilt trim where the wall meets the floor — the top room only. */}
      {s.gilt && <rect x="0" y="212" width="560" height="3" fill={GOLD_DEEP} opacity="0.85" />}
    </g>
  )
}

// ── Furnishings ─────────────────────────────────────────────────────────────
// Every one drawn around its GROUND POINT, except `wall` pieces which straddle
// it. Small: these render at roughly 40px inside a 520px-wide card.

/**
 * Options a furnishing can react to. Only the lampstand uses `lit` today; every
 * other prop ignores it, which is why it is one object rather than a growing
 * argument list.
 */
export interface PropOpts {
  /** Whether the lampstand is burning — see LAMP below. */
  lit: boolean
}

type Prop = (o: PropOpts) => JSX.Element

const PROPS: Record<string, Prop> = {
  room_reed_mat: () => (
    <g>
      <ellipse cx="0" cy="-3" rx="34" ry="9" fill="#9a8455" />
      <ellipse cx="0" cy="-4" rx="28" ry="6.5" fill="#b39a66" />
      <g stroke="#7d6a44" strokeWidth="1">
        <line x1="-24" y1="-4" x2="24" y2="-4" />
        <line x1="-20" y1="-7" x2="20" y2="-7" />
        <line x1="-20" y1="-1" x2="20" y2="-1" />
      </g>
    </g>
  ),
  room_stool: () => (
    <g>
      <ellipse cx="0" cy="-1" rx="15" ry="4" fill={WOOD_DARK} opacity="0.5" />
      <rect x="-13" y="-20" width="26" height="6" rx="2" fill={WOOD} />
      <rect x="-11" y="-14" width="4" height="14" fill={WOOD_DARK} />
      <rect x="7" y="-14" width="4" height="14" fill={WOOD_DARK} />
      <rect x="-2" y="-14" width="4" height="13" fill={WOOD_DARK} />
    </g>
  ),
  room_water_jar: () => (
    <g>
      <ellipse cx="0" cy="-1" rx="13" ry="4" fill={WOOD_DARK} opacity="0.4" />
      <path d="M-11 -4 q-4 -18 4 -24 h14 q8 6 4 24 z" fill="#a2603f" />
      <path d="M-7 -26 h14 v4 h-14 z" fill="#8a4f33" />
      <ellipse cx="0" cy="-26" rx="7" ry="2.6" fill="#7a4429" />
      <path d="M-11 -12 q11 4 22 0" stroke="#8a4f33" strokeWidth="1.6" fill="none" />
    </g>
  ),
  room_loom: () => (
    <g>
      <rect x="-18" y="-34" width="4" height="34" fill={WOOD_DARK} />
      <rect x="14" y="-34" width="4" height="34" fill={WOOD_DARK} />
      <rect x="-20" y="-36" width="40" height="4" rx="1" fill={WOOD} />
      <rect x="-14" y="-30" width="28" height="17" fill={CLOTH_2} />
      <g stroke={LINEN} strokeWidth="1" opacity="0.75">
        <line x1="-12" y1="-30" x2="-12" y2="-6" />
        <line x1="-6" y1="-30" x2="-6" y2="-6" />
        <line x1="0" y1="-30" x2="0" y2="-6" />
        <line x1="6" y1="-30" x2="6" y2="-6" />
        <line x1="12" y1="-30" x2="12" y2="-6" />
      </g>
    </g>
  ),
  // THE LAMP. Burning when you have prayed today, a cold wick when you haven't.
  //
  // This is the whole of the feedback for praying, and what it is NOT is the
  // point: it is not a count, not a streak, not a rung, and not a number that
  // can stall. It resets every day by itself, it lives in the room nobody else
  // can change, and the only thing it can ever say is "today, yes" or nothing
  // at all. That is the keep's "presence, not quantity" rule applied to the one
  // feature in this app where a growing tally would change WHY somebody does it.
  //
  // The glow is flat circles rather than a filter, for the same reason
  // everything else in here is: no <defs>, and drop-shadow on an SVG this size
  // repaints more than it is worth.
  room_lampstand: ({ lit }) => (
    <g>
      <ellipse cx="0" cy="-1" rx="9" ry="3" fill={BRASS} />
      <rect x="-2" y="-26" width="4" height="25" fill={BRASS} />
      <path d="M-11 -26 q11 -7 22 0 z" fill={BRASS} />
      <ellipse cx="0" cy="-27" rx="6" ry="2.4" fill="#a8842f" />
      {/* The wick is always drawn: unlit reads as a lamp waiting, never as a
          broken one. Nothing scolds, nothing is missing. */}
      <rect x="-1" y="-33" width="2" height="6" rx="1" fill="#4a4030" />
      <ellipse cx="0" cy="-33.5" rx="2.2" ry="1.2" fill="#3a3226" />
      {/* The flame is OWNER-ONLY. Only your own room passes `lit`, and the
          group is `data-room-private`, which the postcard strips — so a
          shared picture of your room shows the wick, never the flame. */}
      {lit && (
        <g data-room-private="">
          <circle cx="0" cy="-34" r="16" fill={FLAME} opacity="0.10" />
          <circle cx="0" cy="-34" r="9" fill={FLAME_HOT} opacity="0.16" />
          <path d="M0 -40 q6 6 0 11 q-6 -5 0 -11 z" fill={FLAME} />
          <path d="M0 -36 q3 3 0 6 q-3 -3 0 -6 z" fill={FLAME_HOT} />
        </g>
      )}
    </g>
  ),
  room_open_scroll: () => (
    <g>
      <rect x="-20" y="-8" width="40" height="8" rx="2" fill={LINEN} />
      <rect x="-23" y="-10" width="6" height="12" rx="3" fill={WOOD} />
      <rect x="17" y="-10" width="6" height="12" rx="3" fill={WOOD} />
      <g stroke="#9a8a63" strokeWidth="1">
        <line x1="-14" y1="-6" x2="12" y2="-6" />
        <line x1="-14" y1="-3.5" x2="8" y2="-3.5" />
      </g>
    </g>
  ),
  room_censer: () => (
    <g>
      <path d="M-9 -8 q0 -10 9 -10 q9 0 9 10 z" fill={BRASS} />
      <rect x="-10" y="-9" width="20" height="3" rx="1.5" fill="#a8842f" />
      <path d="M0 -18 v-8" stroke="#a8842f" strokeWidth="1.6" />
      <path d="M-4 -26 q4 -6 8 -2 q-3 5 -8 2 z" fill="#cfd6e6" opacity="0.55" />
      <path d="M-2 -34 q5 -5 7 0" stroke="#cfd6e6" strokeWidth="1.4" fill="none" opacity="0.4" />
    </g>
  ),
  room_olive_jar: () => (
    <g>
      <path d="M-8 -3 q-3 -13 3 -17 h10 q6 4 3 17 z" fill={GREEN} />
      <rect x="-5" y="-22" width="10" height="3" fill="#5c7844" />
      <ellipse cx="0" cy="-22" rx="5" ry="1.8" fill="#4e6839" />
      <ellipse cx="0" cy="-11" rx="8.5" ry="3" fill="#7fa05e" opacity="0.5" />
    </g>
  ),
  room_scroll_rack: () => (
    <g>
      <rect x="-16" y="-22" width="32" height="22" rx="2" fill={WOOD} />
      <rect x="-16" y="-22" width="32" height="3" fill={WOOD_DARK} />
      <g fill={LINEN}>
        <rect x="-12" y="-17" width="6" height="15" rx="3" />
        <rect x="-3" y="-17" width="6" height="15" rx="3" />
        <rect x="6" y="-17" width="6" height="15" rx="3" />
      </g>
      <g fill="#c7b184">
        <circle cx="-9" cy="-17" r="3" />
        <circle cx="0" cy="-17" r="3" />
        <circle cx="9" cy="-17" r="3" />
      </g>
    </g>
  ),
  room_clay_lamps: () => (
    <g>
      {[-14, 0, 14].map((x) => (
        <g key={x} transform={`translate(${x},0)`}>
          <path d="M-6 -4 q0 -5 6 -5 q6 0 6 5 z" fill="#a2603f" />
          <path d="M6 -6 l4 1 l-4 1 z" fill="#8a4f33" />
          <path d="M9 -9 q3 3 0 5 q-3 -2 0 -5 z" fill={FLAME} />
        </g>
      ))}
    </g>
  ),
  room_lattice: () => (
    <g>
      <rect x="-26" y="-30" width="52" height="30" rx="2" fill={WOOD_DARK} opacity="0.9" />
      <rect x="-23" y="-27" width="46" height="24" fill={NIGHT} />
      <g stroke={WOOD} strokeWidth="2">
        <line x1="-23" y1="-15" x2="23" y2="-15" />
        <line x1="-8" y1="-27" x2="-8" y2="-3" />
        <line x1="8" y1="-27" x2="8" y2="-3" />
      </g>
      <circle cx="0" cy="-21" r="2" fill={STAR} opacity="0.7" />
    </g>
  ),
  room_dovecote: () => (
    <g>
      <rect x="-16" y="-26" width="32" height="26" rx="2" fill={WOOD} />
      <path d="M-19 -26 h38 l-19 -12 z" fill={WOOD_DARK} />
      <circle cx="-7" cy="-16" r="4.5" fill={NIGHT} />
      <circle cx="7" cy="-16" r="4.5" fill={NIGHT} />
      <rect x="-16" y="-8" width="32" height="3" fill={WOOD_DARK} />
      <g fill={LINEN}>
        <ellipse cx="14" cy="-30" rx="6" ry="4" />
        <circle cx="19" cy="-33" r="2.6" />
        <path d="M9 -31 q4 -5 8 -1 z" fill="#cfc4a8" />
      </g>
    </g>
  ),
  room_sleeping_mat: () => (
    <g>
      <ellipse cx="0" cy="-2" rx="30" ry="8" fill="#7a6448" />
      <rect x="-28" y="-12" width="56" height="10" rx="5" fill={CLOTH} />
      <rect x="-28" y="-12" width="20" height="10" rx="5" fill={LINEN} />
      <path d="M-26 -12 q26 -6 52 0" stroke="#7d4636" strokeWidth="1.4" fill="none" />
    </g>
  ),
  room_cedar_chest: () => (
    <g>
      <rect x="-24" y="-24" width="48" height="24" rx="2" fill="#7b5230" />
      <path d="M-24 -24 q24 -10 48 0 z" fill="#8d6039" />
      <rect x="-24" y="-13" width="48" height="4" fill={BRASS} />
      <rect x="-3" y="-16" width="6" height="9" rx="1" fill={BRASS} />
      <g fill={BRASS}>
        <rect x="-24" y="-24" width="4" height="24" />
        <rect x="20" y="-24" width="4" height="24" />
      </g>
    </g>
  ),
  // ── Wall pieces: drawn AROUND the point, not standing on it ──
  room_hanging: () => (
    <g>
      <rect x="-22" y="-26" width="44" height="4" rx="2" fill={WOOD_DARK} />
      <path d="M-20 -22 h40 v40 l-20 -8 l-20 8 z" fill={CLOTH} />
      <rect x="-20" y="-14" width="40" height="6" fill={CLOTH_2} />
      <rect x="-20" y="2" width="40" height="5" fill={CLOTH_2} />
      <circle cx="0" cy="-4" r="5" fill={LINEN} opacity="0.85" />
    </g>
  ),
  room_psaltery: () => (
    <g>
      <path d="M-18 14 q-6 -30 18 -32 q24 2 18 32 z" fill="#8d6039" />
      <path d="M-13 10 q-4 -23 13 -25 q17 2 13 25 z" fill="#a5733f" />
      <g stroke={LINEN} strokeWidth="1" opacity="0.85">
        {[-9, -6, -3, 0, 3, 6, 9].map((x) => (
          <line key={x} x1={x} y1="-13" x2={x} y2="9" />
        ))}
      </g>
      <rect x="-16" y="-18" width="32" height="4" rx="2" fill={BRASS} />
    </g>
  ),
  room_land_map: () => (
    <g>
      <rect x="-26" y="-20" width="52" height="40" rx="2" fill={LINEN} />
      <rect x="-26" y="-20" width="52" height="40" rx="2" fill="none" stroke="#b8a37a" strokeWidth="2" />
      <path d="M-16 12 q4 -20 -2 -30" stroke="#7f96a8" strokeWidth="2.4" fill="none" />
      <path d="M6 -14 q10 12 4 26" stroke="#a8956c" strokeWidth="1.6" fill="none" />
      <circle cx="-4" cy="-2" r="2.4" fill={CLOTH} />
      <circle cx="10" cy="8" r="2" fill={CLOTH} />
    </g>
  ),
  room_palm_wreath: () => (
    <g>
      <circle cx="0" cy="0" r="19" fill="none" stroke={GREEN} strokeWidth="6" />
      <circle cx="0" cy="0" r="19" fill="none" stroke="#87a866" strokeWidth="2" />
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <ellipse
          key={a}
          cx={Math.cos((a * Math.PI) / 180) * 19}
          cy={Math.sin((a * Math.PI) / 180) * 19}
          rx="6"
          ry="3"
          fill="#87a866"
          transform={`rotate(${a} ${Math.cos((a * Math.PI) / 180) * 19} ${Math.sin((a * Math.PI) / 180) * 19})`}
        />
      ))}
      <rect x="-3" y="-24" width="6" height="6" rx="2" fill={CLOTH} />
    </g>
  ),
}

/** Every furnishing id RoomArt can draw — used to sanity-check the catalog. */
export const DRAWN_FURNISHINGS = Object.keys(PROPS)

/**
 * Furnishings that stay DRAWN whatever art exists for them.
 *
 * The lampstand carries the prayer signal — it burns when you have prayed today
 * and shows a cold wick when you have not — and a baked image has exactly one
 * state. Same class of exclusion the keep makes for the gonfalon and the
 * destrier's barding, which take denominationColor() at runtime: a picture
 * cannot answer a question asked after it was painted.
 */
const ALWAYS_DRAWN = new Set(['room_lampstand'])

/**
 * Owner-supplied paintings (2026-10-21) that replace five furnishings' renders.
 * ART ONLY: the ids, names, unlock rules, anchors and sizes (RASTER_H) are the
 * furnishings' own, so a piece stands where it always did. Hand-kept rather
 * than in GENERATED_ART, so a gen-art run can never overwrite them.
 */
const DECOR_ART: Record<string, string> = {
  room_hanging: decorBanner,
  room_scroll_rack: decorBooks,
  room_cedar_chest: decorChest,
  room_water_jar: decorPlant,
  room_stool: decorStool,
}

/**
 * Natural height of each furnishing in room units, so a render occupies the
 * space its drawing did rather than whatever size it happened to come back.
 *
 * Measured off the drawn props: the stool stands 20 units, the loom 36, a row
 * of clay lamps 9. A render is given a box three times as wide as it is tall
 * and fits inside it, so its own proportions decide the width — the same
 * bargain ChurchFlora makes, and for the same reason. Forcing every prop into
 * one box would squash a loom and inflate a scroll.
 */
const RASTER_H: Record<string, number> = {
  room_reed_mat: 8,
  room_stool: 32, // owner-supplied painting (DECOR_ART), drawn larger than the old render
  room_sleeping_mat: 10,
  room_olive_jar: 16,
  room_lattice: 22,
  room_hanging: 48, // owner-supplied painting (DECOR_ART), drawn larger than the old render
  room_open_scroll: 8,
  room_scroll_rack: 29, // owner-supplied painting (DECOR_ART), drawn larger than the old render
  room_water_jar: 35, // owner-supplied painting (DECOR_ART), drawn larger than the old render
  room_clay_lamps: 9,
  room_psaltery: 22,
  room_palm_wreath: 24,
  room_censer: 14,
  room_land_map: 26,
  room_dovecote: 24,
  room_loom: 36,
  room_cedar_chest: 32, // owner-supplied painting (DECOR_ART), drawn larger than the old render
}

/** A render placed on the same ground point its drawing is built around. */
function PropImage({ id, mount, href, h }: { id: string; mount: RoomMount; href: string; h: number }) {
  const w = h * 3
  // Everything stands ON the ground point; only a wall piece straddles it.
  return (
    <image
      href={href}
      x={-w / 2}
      y={mount === 'wall' ? -h / 2 : -h}
      width={w}
      height={h}
      preserveAspectRatio={mount === 'wall' ? 'xMidYMid meet' : 'xMidYMax meet'}
      aria-hidden
      data-prop={id}
    />
  )
}

/** The render for a furnishing, or null to keep its drawing. */
function propRaster(id: string): string | null {
  if (ALWAYS_DRAWN.has(id)) return null
  return DECOR_ART[id] ?? GENERATED_ART[id] ?? null
}

/** Plain, Fine, Grand. Small steps: this has to stay recognisably the same mat. */
const TIER_SCALE = [1, 1.12, 1.24]

/**
 * What "finer" looks like, in flat fills like everything else in here. Ground
 * pieces gain a gilt mat under them; hung pieces gain a gilt mounting behind.
 * Grand adds a second ring and studs — the SHAPE changes, so the step never
 * relies on colour alone (the chart rule).
 */
function TierAccent({ tier, mount }: { tier: number; mount?: RoomMount }) {
  if (mount === 'wall') {
    return (
      <g>
        <circle cx="0" cy="0" r="26" fill={GOLD_DEEP} opacity="0.26" />
        {tier > 2 && (
          <g fill={GOLD}>
            <circle cx="0" cy="0" r="30" opacity="0.18" />
            <circle cx="-21" cy="-21" r="2" />
            <circle cx="21" cy="-21" r="2" />
            <circle cx="-21" cy="21" r="2" />
            <circle cx="21" cy="21" r="2" />
          </g>
        )}
      </g>
    )
  }
  return (
    <g>
      <ellipse cx="0" cy="0" rx="26" ry="7" fill={GOLD_DEEP} opacity="0.3" />
      {tier > 2 && <ellipse cx="0" cy="0" rx="31" ry="9" fill={GOLD} opacity="0.2" />}
    </g>
  )
}

/**
 * One furnishing, in the room. `value` is the PACKED placement
 * (`room_reed_mat.2`), so tier and id arrive together and no caller has to
 * remember to unpack. A merged piece is the same object grown and gilded rather
 * than a second drawing — 18 furnishings x 3 tiers would be 54 pictures for
 * something drawn at 40px.
 */
// THE DOOR LANTERN. Lit when somebody knelt at your Prayer Wall note in the
// last day (`my_prayer_wall` → `mine.lit`, 0099); dark when nobody has.
//
// The lamp's shape exactly, and for the lamp's reasons: not a count, not a
// name, not a brighter flame for more prayers — "today, yes" or nothing. It
// is NOT a furnishing: nothing earns it and the shelf never offers it. It is
// drawn only in your own room (RoomScene's `lantern`, passed by RoomSection
// and nobody else) and only while your note is in the wall, so a visitor, a
// postcard and a keyless build never see it at all.
//
// None of the five room paintings shows a door — the way in is the viewer's
// side — so it hangs off the frame's edge on an iron bracket, the doorpost
// you came in by. Drawn around the bracket's wall point; the arm reaches left.
/** Forged black iron: the room's grey IRON read as a pipe against warm stone. */
const LANTERN_IRON = '#2e2622'

export function DoorLantern({ x, y, scale = 1, lit }: { x: number; y: number; scale?: number; lit: boolean }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <title>{lit ? 'The lantern by your door: somebody prayed over your note today' : 'The lantern by your door'}</title>
      {/* Bracket: a plate on the post and an arm out over the floor. */}
      <rect x="-3" y="-6" width="6" height="14" rx="1.5" fill={LANTERN_IRON} />
      <path d="M0 -2 h-18 q-4 0 -4 4" stroke={LANTERN_IRON} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      <path d="M0 4 q-10 0 -14 -6" stroke={LANTERN_IRON} strokeWidth="1.6" fill="none" />
      <line x1="-22" y1="2" x2="-22" y2="6" stroke={LANTERN_IRON} strokeWidth="1.4" />
      {lit && (
        <>
          <circle cx="-22" cy="17" r="15" fill={FLAME} opacity="0.10" />
          <circle cx="-22" cy="17" r="8.5" fill={FLAME_HOT} opacity="0.16" />
        </>
      )}
      {/* The lantern: cap, glass, base. */}
      <path d="M-28 8 h12 l-2 -3 h-8 z" fill={BRASS} />
      <rect x="-28" y="8" width="12" height="17" rx="1.5" fill={lit ? '#5a4318' : '#2c2a2e'} opacity="0.92" />
      <rect x="-26.5" y="9.5" width="9" height="14" rx="1" fill={lit ? '#ffcf6a' : '#3c3a40'} opacity={lit ? 0.55 : 0.9} />
      <line x1="-22" y1="8" x2="-22" y2="25" stroke={BRASS} strokeWidth="1" opacity="0.8" />
      <rect x="-29" y="25" width="14" height="3" rx="1" fill={BRASS} />
      {lit ? (
        <>
          <path d="M-22 12 q4 4.5 0 9 q-4 -4.5 0 -9 z" fill={FLAME} />
          <path d="M-22 15.5 q2 2.2 0 4.4 q-2 -2.2 0 -4.4 z" fill={FLAME_HOT} />
        </>
      ) : (
        // Waiting, never broken: the wick is there, just dark.
        <rect x="-22.8" y="18" width="1.6" height="4" rx="0.8" fill="#4a4030" />
      )}
    </g>
  )
}

export function FurnishingProp({
  value,
  x,
  y,
  mount,
  lit = false,
  sizeScale = 1,
}: {
  value: string
  x: number
  y: number
  mount?: RoomMount
  /** The player-chosen size from the placement value, on top of the tier's. */
  sizeScale?: number
  /**
   * Whether the lamp is burning.
   *
   * DEFAULTS TO FALSE, and that default is the privacy rule. Only your OWN
   * room (RoomSection) passes the real value; a visited room and every other
   * render draw the cold wick, and the postcard strips the flame even from
   * your own room (`data-room-private`). Praying is a personal practice, so
   * "did they pray today" is readable on exactly one screen: yours.
   */
  lit?: boolean
}) {
  const { id, tier } = unpackDecor(value)
  const art = PROPS[id]
  const raster = propRaster(id)
  if (!art && !raster) return null
  const grown = (TIER_SCALE[tier - 1] ?? 1) * sizeScale
  return (
    <g transform={`translate(${x}, ${y})`}>
      {/* Behind the prop, so it reads as the object sitting on something finer
          rather than a highlight painted over it. */}
      {tier > 1 && <TierAccent tier={tier} mount={mount} />}
      <g transform={grown === 1 ? undefined : `scale(${grown})`}>
        {raster ? (
          <PropImage id={id} mount={mount ?? 'floor'} href={raster} h={RASTER_H[id] ?? 20} />
        ) : (
          art?.({ lit })
        )}
      </g>
    </g>
  )
}

/**
 * One furnishing on its own, for the shelf you pick from — the actual object at
 * thumbnail size rather than a name in a list, so what you tap is what you get.
 *
 * The viewBox is chosen per mount because props are drawn around their GROUND
 * POINT: a wall piece straddles (0,0) and everything else stands on it, so one
 * box for both would crop one of them.
 */
export function FurnishingThumb({ id, size = 56 }: { id: string; size?: number }) {
  const def = furnishingById(id)
  const prop = PROPS[id]
  const raster = propRaster(id)
  if (!def || (!prop && !raster)) return null
  const box = def.mount === 'wall' ? '-32 -32 64 64' : '-32 -46 64 56'
  return (
    <svg width={size} height={size} viewBox={box} style={{ display: 'block' }} aria-hidden>
      {/* The shelf shows the object itself, always lit: this is a picture of
          the lampstand, not a report on today. */}
      {raster ? (
        // Sized to the thumbnail box rather than to room units — the shelf is a
        // catalogue, so every tile is the same size on purpose.
        <PropImage id={id} mount={def.mount ?? 'floor'} href={raster} h={def.mount === 'wall' ? 44 : 40} />
      ) : (
        prop?.({ lit: true })
      )}
    </svg>
  )
}

/** The room's name at a tier, for callers that only have the tier. */
export const roomNameForTier = roomTierName
